import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { unwrap } from '@/api/envelope';
import { listOrganizations } from '@/api/generated';
import type {
  OrganizationSortField,
  OrganizationStatus,
  OrganizationSummary,
  OrganizationType,
} from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, type SortState } from '@/components/ui/DataTable';
import { FilterBar } from '@/components/ui/FilterBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { StatCard, StatCardRow } from '@/components/ui/StatCard';
import { useDebounced } from '@/lib/useDebounced';

import { ChangeStatusDialog, type StatusTarget } from './ChangeStatusDialog';
import { useViewPortal } from './useViewPortal';
import {
  STATUS_COLOUR,
  STATUS_LABELS,
  TYPE_LABELS,
  formatDate,
} from '@/lib/organization';

const PAGE_SIZE = 20;

function scopeOf(org: OrganizationSummary) {
  const states = org.geographic_states ?? [];
  if (states.length > 0) return states.join(', ');
  if (org.geographic_scope) return org.geographic_scope.replace('_', ' ').toLowerCase();
  return '—';
}

/**
 * Everything that decides which organizations are shown, in one object.
 *
 * It is also the query key, so any change fetches the right page and nothing
 * has to be kept in step by hand.
 */
interface ListQuery {
  search: string;
  // Empty string means "any", which is what the filter bar uses for its blank
  // option. The generated types are unions, so a typo cannot reach the request.
  status: OrganizationStatus | '';
  type: OrganizationType | '';
  sort: SortState<OrganizationSortField> | null;
  page: number;
}

const INITIAL: ListQuery = {
  search: '',
  status: '',
  type: '',
  // No ordering until staff ask for one, so the server's own default applies
  // and the request stays as small as the list page it replaces.
  sort: null,
  page: 1,
};

export function OrganizationsPage() {
  const navigate = useNavigate();
  const viewPortal = useViewPortal();
  const [listQuery, setListQuery] = useState<ListQuery>(INITIAL);
  /*
   * The row whose status is being changed, or null. One dialog for the whole
   * table rather than one per row: only one can be open, and forty mounted
   * dialogs is forty sets of state to keep in step for no gain.
   */
  const [statusTarget, setStatusTarget] = useState<StatusTarget | null>(null);

  // The search box asks the server, so it waits for a pause in typing. Every
  // keystroke was free when filtering happened in the browser; it is not now.
  const debouncedSearch = useDebounced(listQuery.search, 300);

  const query = useQuery({
    queryKey: [
      'staff',
      'organizations',
      {
        search: debouncedSearch,
        status: listQuery.status,
        type: listQuery.type,
        sort: listQuery.sort,
        page: listQuery.page,
      },
    ],
    queryFn: async () => {
      const reply = await listOrganizations({
        query: {
          page: listQuery.page,
          limit: PAGE_SIZE,
          include_deleted: true,
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
          ...(listQuery.status ? { status: listQuery.status } : {}),
          ...(listQuery.type ? { organization_type: listQuery.type } : {}),
          ...(listQuery.sort
            ? { sort: listQuery.sort.column, direction: listQuery.sort.direction }
            : {}),
        },
        throwOnError: true,
      });
      // unwrap, not reply.data.data. The backend can answer 200 with a fault
      // reported inside the envelope, and reading the payload directly turned
      // that into `undefined` - which the table then showed as "no
      // organizations have been created yet". A server problem read as an
      // empty list.
      return unwrap(reply.data);
    },
    // Keeps the previous page on screen while the next one is fetched, so
    // paging does not blank the table between pages.
    placeholderData: (previous) => previous,
  });

  /*
   * The summary counts are the whole list, not the page, so they are asked for
   * separately. One page cannot be counted by status when it is one page of
   * many, and the endpoint reports a total for any filter, so a request with
   * the smallest possible page gives each count for the price of a row.
   */
  const counts = useQuery({
    queryKey: ['staff', 'organizations', 'counts'],
    queryFn: async () => {
      const totalFor = async (status?: OrganizationStatus) => {
        const reply = await listOrganizations({
          query: { limit: 1, include_deleted: true, ...(status ? { status } : {}) },
          throwOnError: true,
        });
        return unwrap(reply.data).pagination?.total ?? 0;
      };

      const [total, active, pending, deactivated] = await Promise.all([
        totalFor(),
        totalFor('ACTIVE'),
        totalFor('PENDING'),
        totalFor('DEACTIVATED'),
      ]);
      return { total, active, pending, deactivated };
    },
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.pagination?.total ?? 0;

  /** Changes anything that narrows or orders the list, and returns to page one. */
  const narrow = (change: Partial<ListQuery>) =>
    setListQuery((prev) => ({ ...prev, ...change, page: 1 }));

  const columns: Column<OrganizationSummary, OrganizationSortField>[] = [
    {
      key: 'name',
      header: 'Organization',
      sortKey: 'name',
      render: (org) => (
        <Box>
          {/* The name is the way into the account. */}
          <Link
            component={RouterLink}
            to={ROUTES.staff.organization(org.id)}
            variant="body2"
            data-testid="org-list-name-link"
            sx={{ fontWeight: 500 }}
          >
            {org.name}
          </Link>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            data-testid="org-list-slug-value"
          >
            {org.slug}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortKey: 'organization_type',
      render: (org) =>
        org.organization_type
          ? (TYPE_LABELS[org.organization_type] ?? org.organization_type)
          : '—',
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (org) => (
        <Chip
          size="small"
          label={STATUS_LABELS[org.status] ?? org.status}
          color={STATUS_COLOUR[org.status] ?? 'default'}
          variant="outlined"
          data-testid="org-list-status-chip"
        />
      ),
    },
    { key: 'scope', header: 'Scope', sortKey: 'geographic_scope', render: scopeOf },
    {
      key: 'created',
      header: 'Created',
      sortKey: 'created_at',
      render: (org) => formatDate(org.created_at),
    },
    {
      key: 'actions',
      // The column needs a name for the header row, but repeating "Actions"
      // down the table is what the per-row button's own name is for.
      header: 'Actions',
      align: 'right',
      render: (org) => (
        <RowActionsMenu
          rowLabel={org.name}
          testId="org-list-row-actions"
          actions={[
            {
              id: 'open',
              label: 'Open',
              onSelect: () => void navigate(ROUTES.staff.organization(org.id)),
            },
            {
              id: 'change-status',
              label: 'Change status',
              onSelect: () =>
                setStatusTarget({ id: org.id, name: org.name, status: org.status }),
            },
            {
              id: 'view-portal',
              label: 'View portal',
              onSelect: () => viewPortal({ id: org.id, name: org.name }),
            },
          ]}
        />
      ),
    },
  ];

  const filtered = Boolean(debouncedSearch.trim() || listQuery.status || listQuery.type);
  /*
   * A dash while the counts are on their way, and a dash if they never arrive -
   * but the two are not the same thing, and a permanent dash with no error and
   * no way to retry is indistinguishable from "still loading". The cards keep
   * the dash and a message below them says what happened.
   */
  const countOrDash = (value: number | undefined) =>
    counts.isPending || counts.isError || value === undefined ? '—' : value;

  return (
    <Box data-testid="org-list-page">
      <PageHeader
        title="Organizations"
        description="Create and manage the organization accounts on the platform."
        action={
          <Button
            component={RouterLink}
            to={ROUTES.staff.newOrganization}
            startIcon={<AddIcon />}
            data-testid="org-list-new-link"
          >
            New Organization
          </Button>
        }
      />

      <StatCardRow testId="org-list-summary-group">
        {/* A dash rather than 0 until the counts are known. Showing 0 while
            loading reads as "there are none", which is a different claim. */}
        <StatCard
          label="Total"
          testId="org-list-total-card"
          value={countOrDash(counts.data?.total)}
          caption="All organizations"
        />
        <StatCard
          label="Active"
          testId="org-list-active-card"
          value={countOrDash(counts.data?.active)}
          caption="Visible in the app"
        />
        <StatCard
          label="Pending"
          testId="org-list-pending-card"
          value={countOrDash(counts.data?.pending)}
          caption="Not yet activated"
        />
        <StatCard
          label="Deactivated"
          testId="org-list-deactivated-card"
          value={countOrDash(counts.data?.deactivated)}
          caption="Retained, not deleted"
        />
      </StatCardRow>

      {counts.isError ? (
        <Alert
          severity="warning"
          role="status"
          data-testid="org-list-counts-alert"
          sx={{ mb: 3, maxWidth: 760 }}
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void counts.refetch()}
              data-testid="org-list-counts-retry-button"
            >
              Try again
            </Button>
          }
        >
          The summary counts could not be loaded. The list below is unaffected.
        </Alert>
      ) : null}

      <FilterBar
        testId="org-list-filters"
        searchValue={listQuery.search}
        onSearchChange={(search) =>
          // Back to page one as the box changes, not when the delayed request
          // goes out: otherwise a search typed from page four asks for page
          // four of a list that may now have one page.
          setListQuery((prev) => ({ ...prev, search, page: 1 }))
        }
        searchPlaceholder="Search organizations"
        values={{ status: listQuery.status, type: listQuery.type }}
        onFilterChange={(name, value) =>
          // The filter bar deals in strings; the union is re-established
          // here, at the one place a value enters the query state.
          narrow(
            name === 'status'
              ? { status: value as OrganizationStatus | '' }
              : { type: value as OrganizationType | '' },
          )
        }
        onClear={() => setListQuery(INITIAL)}
        filters={[
          {
            name: 'status',
            label: 'Status',
            options: Object.entries(STATUS_LABELS).map(([value, label]) => ({
              value,
              label,
            })),
          },
          {
            name: 'type',
            label: 'Type',
            options: Object.entries(TYPE_LABELS).map(([value, label]) => ({
              value,
              label,
            })),
          },
        ]}
      />

      <DataTable
        testId="org-list-table"
        caption="Organization accounts"
        columns={columns}
        rows={rows}
        getRowKey={(org) => org.id}
        loading={query.isPending}
        // isPending is false for every page after the first, because the
        // previous page is kept on screen as placeholder data. Without this a
        // page change showed nothing at all until the new rows arrived.
        refreshing={query.isFetching && !query.isPending}
        error={query.isError ? 'The organizations could not be loaded.' : null}
        onRetry={() => void query.refetch()}
        emptyMessage="No organizations have been created yet."
        noMatchMessage={
          debouncedSearch.trim()
            ? 'No organizations matched your search.'
            : 'No organizations matched those filters.'
        }
        filtered={filtered}
        sort={listQuery.sort}
        onSortChange={(column) =>
          narrow({
            sort: {
              column,
              // Choosing the column already ordered by reverses it; a new
              // column starts ascending, which reads as A to Z.
              direction:
                listQuery.sort?.column === column && listQuery.sort.direction === 'asc'
                  ? 'desc'
                  : 'asc',
            },
          })
        }
        page={listQuery.page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={(page) => setListQuery((prev) => ({ ...prev, page }))}
      />

      {/* Mounted only while a row is chosen, so it starts empty each time and
          carries no trace of the row before. */}
      {statusTarget ? (
        <ChangeStatusDialog
          organization={statusTarget}
          open
          onClose={() => setStatusTarget(null)}
        />
      ) : null}
    </Box>
  );
}
